import type React from "react";
import { useState } from "react";
import { FormattedHTMLMessage } from "react-intl";

import { SelectedOrg, SearchOverlay } from "@jslib/OrgSearch";
import { getSafe } from "@jslib/shared/utils/getSafe";

import { useMessage } from "@/components/Message";
import { useFlag } from "@/components/FeatureFlags/hooks";

import { dashed } from "../../../lib/utils/strings";
import { FormFieldLayout, getFormFieldLabelHtmlId } from "../FormFieldLayout/FormFieldLayout";
import {
  TypeaheadComponent,
  type TypeaheadProps,
} from "../../FormInputs/Typeahead/TypeaheadComponent";
import { type Locale, type VerificationService } from "../../../lib/types/types";
import { getOptions } from "../../../options/options";
import { getLocaleSafely } from "../../../lib/intl/intl";
import { isCountrySelectionNeeded } from "../Country/countryUtils";
import { isOpenOrganization } from "../../../lib/organization/organization";

// Extend TypeaheadProps to pass along things like required, onKeyUp, etc.
// Omit errorId, isErrored, etc because those are handled within this component
export interface FormFieldTypeaheadComponentProps extends Omit<
  TypeaheadProps,
  | "inputHtmlId"
  | "id"
  | "errorId"
  | "isErrored"
  | "locale"
  | "programId"
  | "country"
  | "orgSearchUrl" // omit b/c this component (FormFieldTypeaheadComponent) has a fallback so orgSearchUrl is optional
  | "onBlur"
> {
  fieldId: string; // should be camelCase
  verificationService: VerificationService;
  label: React.ReactNode; // Label is always required for screen readers
  showPlaceholderAndHideLabel?: boolean;
  errorMessage?: React.ReactNode;
  warningMessage?: React.ReactNode;
  helperMessage?: React.ReactNode;
  explanation?: string | JSX.Element;
  orgSearchUrl: string;
}

/**
 * Generic typeahead field component.
 * In the JSLIB, a field has a label, inline error message, and an input of some kind.
 */
export const FormFieldTypeahead = ({
  fieldId,
  value,
  verificationService,
  onChange,
  placeholder,
  label,
  errorMessage,
  warningMessage,
  helperMessage,
  showPlaceholderAndHideLabel = false,
  isRequired = true,
  explanation = "",
  orgSearchUrl,
  urlAddSchoolForm,
  disabled = false,
}: FormFieldTypeaheadComponentProps) => {
  const { msg } = useMessage();

  const labelHtmlId = `sid-${dashed(fieldId)}`;
  const errorHtmlId = `sid-${dashed(fieldId)}-error`;
  const warningHtmlId = `sid-${dashed(fieldId)}-warning`;
  const { programId, programTheme, verificationResponse, viewModel } = verificationService;
  const countryChoiceValue = getSafe(() => viewModel.countryChoice.value);
  const needToPickCountry = isCountrySelectionNeeded(programTheme, countryChoiceValue);
  const locale: Locale = getLocaleSafely(viewModel, verificationResponse);
  const [autoFocus, setAutoFocus] = useState(false);
  const showSearchOverlay = useFlag("org-search-overlay") === "overlay";
  const showSelectedOrgBox = showSearchOverlay || useFlag("org-selected-display") === "box-display";
  const showOpenOrgWarningOrange = useFlag("hc-open-org-warning") === "warning-orange";
  const showOpenOrgWarningGray = useFlag("hc-open-org-warning") === "warning-gray";
  const showOpenOrgWarningFlag = showOpenOrgWarningOrange || showOpenOrgWarningGray;

  const [showOpenOrgWarning, setShowOpenOrgWarning] = useState(false);
  const openOrgSearchEnabled = getSafe(() => programTheme.openOrgSearchEnabled);

  const handleBlur = () => {
    if (openOrgSearchEnabled && isOpenOrganization(value)) {
      setShowOpenOrgWarning(true);
    } else {
      setShowOpenOrgWarning(false);
    }
  };

  const handleChange = (newValue: typeof value) => {
    if (showOpenOrgWarning) {
      setShowOpenOrgWarning(false);
    }
    onChange(newValue);
  };

  // Combine prop warning with internal open org warning
  const openOrgWarningMessage = showOpenOrgWarning && showOpenOrgWarningFlag && (
    <FormattedHTMLMessage
      id="warningId.openOrganization"
      defaultMessage="We couldn't find a match for that name in our database. Please double-check the spelling, if it's correct, feel free to continue!"
    />
  );
  const warningMsg = warningMessage || (showOpenOrgWarningOrange && openOrgWarningMessage);
  const helperMsg = helperMessage || (showOpenOrgWarningGray && openOrgWarningMessage);

  const getClassName = () => {
    if (errorMessage) return "sid-text-input--error";
    if (warningMsg) return "sid-text-input--warning";
    return "";
  };

  const getAriaDescribedBy = () => {
    if (errorMessage) return errorHtmlId;
    if (warningMsg) return warningHtmlId;
    return undefined;
  };

  return (
    <SearchOverlay
      enabled={showSearchOverlay}
      closeSectionLabel={<FormattedHTMLMessage id="closeSection" defaultMessage="Close Section" />}
    >
      <FormFieldLayout
        fieldId={fieldId}
        label={label}
        errorMessage={errorMessage}
        warningMessage={warningMsg}
        helperMessage={helperMsg}
        showPlaceholderAndHideLabel={showPlaceholderAndHideLabel}
        isRequired={isRequired || needToPickCountry}
        explanation={
          needToPickCountry ? (
            <FormattedHTMLMessage
              id="chooseCountry"
              defaultMessage="Choose a country before searching"
            />
          ) : (
            explanation
          )
        }
        containerId={`${labelHtmlId}-id`}
      >
        {showSelectedOrgBox && value.id ? (
          <SelectedOrg
            org={value}
            onClear={() => {
              handleChange(undefined);
              setAutoFocus(true);
            }}
            clearSelectionLabel={msg({ id: "clearSelection" })}
            labelId={getFormFieldLabelHtmlId(fieldId)}
          />
        ) : (
          <TypeaheadComponent
            className={getClassName()}
            disabled={needToPickCountry || disabled}
            countryCode={countryChoiceValue}
            minimumSearchValueLength={getOptions().minimumOrganizationSearchLength}
            onChange={handleChange}
            placeholder={placeholder}
            programId={programId}
            value={value}
            inputHtmlId={labelHtmlId}
            openOrgSearchEnabled={programTheme.openOrgSearchEnabled}
            locale={locale}
            orgSearchUrl={orgSearchUrl || getSafe(() => programTheme.config.orgSearchUrl)}
            urlAddSchoolForm={urlAddSchoolForm}
            isRequired={isRequired}
            isErrored={!!errorMessage}
            ariaDescribedBy={getAriaDescribedBy()}
            autoFocus={autoFocus}
            onBlur={handleBlur}
          />
        )}
      </FormFieldLayout>
    </SearchOverlay>
  );
};
